////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// fantasy-cipher.ts — FANTASY LANGUAGE CIPHER (client preview)
// ----------------------------------------------------------------------------------------------------
// TS port of includes/js/fantasy-cipher.js (Wave 2a of plans/bundler-script-load-order-phase2.md),
// itself extracted verbatim from includes/js/template.js (originally lines ~42356-42636). Mirrors PHP
// LanguageScript::cipher; reads window.FateTide.language.byUuid rendered into the page by the footer PHP.
//
// FOLDED as the FIRST member of the post-template-forms aggregate (it must load before quill-init, which
// reads ftUpdateInventoryDescriptionScriptPreview — call-time, but order kept for safety/parity).
//
// MODULE-SCOPE SAFE (clean 2a): eval-time work = the 7 window attaches + two run-once-guarded document
// listeners (change on #inventory_written_in_language; click on .ft-written-translate-btn) — pure DOM
// wiring. The window.FateTide.language reads are ALL optional-chained AND inside function bodies (call
// time), so FateTide need not even exist at eval. No template.js module-scope reads.
//
// PUBLIC GLOBALS (window-attached — exact surface of the original IIFE's `global.X =` set):
//   ftLanguageApplyCipher, ftLanguageGetCssClass, ftLatinToTengwarQwertyOutsideTags,
//   ftLatinToDwarvishRunicOutsideTags, ftLatinToCelestialEnochianOutsideTags, ftLanguageWrapPreview,
//   ftUpdateInventoryDescriptionScriptPreview.  (ftLanguageNormalizeUuid stays module-private, as it was
//   IIFE-private in the original.)
////////////////////////////////////////////////////////////////////////////////////////////////////////////////

type CipherText = string | null | undefined;

function ftLanguageNormalizeUuid(languageId: unknown): string {
    if (!languageId || typeof languageId !== 'string') {
        return '';
    }
    return languageId.trim().toUpperCase();
}

/**
 * Apply the same a–z substitution as PHP LanguageScript::cipher (skips [tag] segments).
 */
function ftLanguageApplyCipher(text: CipherText, languageId: unknown): CipherText {
    if (text == null || text === '') {
        return text;
    }
    const id = ftLanguageNormalizeUuid(languageId);
    const cfg = window.FateTide?.language?.byUuid?.[id];
    if (!cfg || typeof cfg.perm !== 'string' || cfg.perm.length !== 26) {
        return text;
    }
    const perm = cfg.perm;
    if (perm === 'abcdefghijklmnopqrstuvwxyz') {
        return text;
    }

    const map: Record<string, string> = {};
    for (let i = 0; i < 26; i++) {
        map[String.fromCharCode(97 + i)] = perm.charAt(i);
    }

    const parts = text.split(/(\[[^\]]*\])/);
    let out = '';
    for (const part of parts) {
        if (part !== '' && part[0] === '[') {
            out += part;
            continue;
        }
        for (let j = 0; j < part.length; j++) {
            const c = part.charAt(j);
            const code = c.charCodeAt(0);
            if (code >= 97 && code <= 122) {
                out += map[c] || c;
            } else if (code >= 65 && code <= 90) {
                const lc = String.fromCharCode(code + 32);
                const sub = map[lc] || lc;
                out += sub.toUpperCase();
            } else {
                out += c;
            }
        }
    }
    return out;
}

function ftLanguageGetCssClass(languageId: unknown): string {
    const id = ftLanguageNormalizeUuid(languageId);
    return window.FateTide?.language?.byUuid?.[id]?.css || '';
}

/**
 * Latin typed on a US keyboard → Free Tengwar tengwarQWERTY PUA (matches PHP LanguageScript).
 * Preserves [...] and <...> segments (FateTide tags / stored HTML).
 */
function ftLatinToTengwarQwertyOutsideTags(text: CipherText): CipherText {
    if (text == null || text === '') {
        return text;
    }
    const macByCh = window.FateTide?.language?.macKeyByChar || {};
    const tengByMac = window.FateTide?.language?.macTengwar || {};
    const parts = text.split(/(\[[^\]]*\]|<[^>]+>)/);
    let out = '';
    for (const part of parts) {
        if (part !== '' && (part[0] === '[' || part[0] === '<')) {
            out += part;
            continue;
        }
        for (const ch of part) {
            const mac = macByCh[ch] !== undefined ? macByCh[ch] : macByCh[ch.toLowerCase()];
            if (mac !== undefined && mac !== null && tengByMac[String(mac)] !== undefined) {
                out += tengByMac[String(mac)];
            } else {
                out += ch;
            }
        }
    }
    return out;
}

/**
 * Latin a–z → Unicode runic (BabelStone); matches PHP LanguageScript::applyDwarvishRunicOutsideTags.
 */
function ftLatinToDwarvishRunicOutsideTags(text: CipherText): CipherText {
    const map = window.FateTide?.language?.dwarvishLatinToRunic || {};
    if (text == null || text === '' || !map || typeof map !== 'object') {
        return text;
    }
    const parts = text.split(/(\[[^\]]*\]|<[^>]+>)/);
    let out = '';
    for (const part of parts) {
        if (part !== '' && (part[0] === '[' || part[0] === '<')) {
            out += part;
            continue;
        }
        for (let j = 0; j < part.length; j++) {
            const c = part.charAt(j);
            const code = c.charCodeAt(0);
            if (code >= 97 && code <= 122 && map[c]) {
                out += map[c];
            } else if (code >= 65 && code <= 90) {
                const lc = String.fromCharCode(code + 32);
                out += map[lc] || c;
            } else {
                out += c;
            }
        }
    }
    return out;
}

/**
 * Latin a–z → Glumbosch Enochian key chars (matches PHP LanguageScript::applyCelestialEnochianOutsideTags).
 */
function ftLatinToCelestialEnochianOutsideTags(text: CipherText): CipherText {
    const map = window.FateTide?.language?.celestialLatinToEnochianKey || {};
    if (text == null || text === '' || !map || typeof map !== 'object') {
        return text;
    }
    const parts = text.split(/(\[[^\]]*\]|<[^>]+>)/);
    let out = '';
    for (const part of parts) {
        if (part !== '' && (part[0] === '[' || part[0] === '<')) {
            out += part;
            continue;
        }
        for (let j = 0; j < part.length; j++) {
            const c = part.charAt(j);
            const code = c.charCodeAt(0);
            if (code >= 97 && code <= 122 && map[c]) {
                out += map[c];
            } else if (code >= 65 && code <= 90) {
                const lc = String.fromCharCode(code + 32);
                out += map[lc] || lc;
            } else {
                out += c;
            }
        }
    }
    return out;
}

function ftLanguageScriptText(text: CipherText, languageId: unknown): CipherText {
    let body = ftLanguageApplyCipher(text, languageId);
    const css = ftLanguageGetCssClass(languageId);
    if (css === 'script-elvish') {
        body = ftLatinToTengwarQwertyOutsideTags(body);
    } else if (css === 'script-dwarvish') {
        body = ftLatinToDwarvishRunicOutsideTags(body);
    } else if (css === 'script-celestial') {
        body = ftLatinToCelestialEnochianOutsideTags(body);
    }
    return body;
}

/** Plain-text preview: cipher + script transforms + span (no parse_text). */
function ftLanguageWrapPreview(text: CipherText, languageId: unknown): CipherText {
    const body = ftLanguageScriptText(text, languageId);
    const css = ftLanguageGetCssClass(languageId);
    if (!css) {
        return body;
    }
    const span = document.createElement('span');
    span.className = 'ft-language-script ' + css;
    span.textContent = body ?? '';
    return span.outerHTML;
}

/** Written cards use the same transforms with the page's rich-text parser/mount. */
function ftLanguageRenderParsed(text: string, languageId: unknown, parse: (text: string) => string): string {
    if (!text) return '';
    const css = ftLanguageGetCssClass(languageId);
    // Keep Quill markup, entities and FateTide link/tag targets intact while rendering their text in script.
    const body = css ? text.split(/(\[[^\]]*\](?:\([^)]*\))?|<[^>]+>|&(?:#\d+|#x[\da-f]+|\w+);)/gi)
        .map(part => /^[\[<&]/.test(part) ? part : ftLanguageScriptText(part, languageId) ?? '').join('') : text;
    const html = parse(body);
    return css ? '<span class="ft-language-script ' + css + '">' + html + '</span>' : html;
}

/**
 * Live preview on Edit Inventory: language + Quill inventory description (plain text + cipher + script font).
 */
function ftUpdateInventoryDescriptionScriptPreview(container: HTMLElement): void {
    if (!container || typeof ftLanguageApplyCipher !== 'function') {
        return;
    }
    const wrap = container.querySelector<HTMLElement>('#div_inventory_description_script_preview_wrap');
    const out = container.querySelector<HTMLElement>('#div_inventory_description_script_preview');
    if (!wrap || !out) {
        return;
    }

    const langEl = container.querySelector<HTMLInputElement | HTMLSelectElement>('#inventory_written_in_language');
    const langId = (langEl && langEl.value) ? String(langEl.value).trim() : '';
    const cssClass = langId ? ftLanguageGetCssClass(langId) : '';

    let plain = '';
    const quillHost = container.querySelector('.quill-editor[data-layer-type-name="inventory_description"]');
    if (typeof Quill !== 'undefined' && quillHost) {
        const q = Quill.find(quillHost);
        if (q) {
            plain = q.getText() || '';
            plain = plain.replace(/\r\n/g, '\n').replace(/\n+$/, '');
        }
    }
    if (!plain.trim()) {
        const ta = container.querySelector<HTMLTextAreaElement>('#inventory_description');
        if (ta && ta.value) {
            const tmp = document.createElement('div');
            tmp.innerHTML = ta.value;
            plain = (tmp.textContent || '').replace(/\r\n/g, '\n').replace(/\n+$/, '');
        }
    }

    if (!langId || !cssClass) {
        wrap.style.display = 'none';
        out.innerHTML = '';
        return;
    }

    wrap.style.display = 'block';
    if (!plain.trim()) {
        out.innerHTML = '<span class="text-muted fst-italic">Type in Inventory Description to preview the script.</span>';
        return;
    }

    out.innerHTML = ftLanguageWrapPreview(plain, langId) ?? '';
}

declare global {
    interface Window {
        FateTide?: {
            language?: {
                byUuid?: Record<string, { perm?: string; css?: string }>;
                macKeyByChar?: Record<string, string | number>;
                macTengwar?: Record<string, string>;
                dwarvishLatinToRunic?: Record<string, string>;
                celestialLatinToEnochianKey?: Record<string, string>;
            };
        };
        ftLanguageApplyCipher: typeof ftLanguageApplyCipher;
        ftLanguageGetCssClass: typeof ftLanguageGetCssClass;
        ftLatinToTengwarQwertyOutsideTags: typeof ftLatinToTengwarQwertyOutsideTags;
        ftLatinToDwarvishRunicOutsideTags: typeof ftLatinToDwarvishRunicOutsideTags;
        ftLatinToCelestialEnochianOutsideTags: typeof ftLatinToCelestialEnochianOutsideTags;
        ftLanguageWrapPreview: typeof ftLanguageWrapPreview;
        ftLanguageRenderParsed: typeof ftLanguageRenderParsed;
        ftMountWrittenDescriptions?: (root?: Element | Document) => void;
        ftUpdateInventoryDescriptionScriptPreview: typeof ftUpdateInventoryDescriptionScriptPreview;
    }
    interface Document {
        ftInventoryScriptPreviewChangeBound?: boolean;
        ftWrittenTranslateClickBound?: boolean;
    }
}

window.ftLanguageApplyCipher = ftLanguageApplyCipher;
window.ftLanguageGetCssClass = ftLanguageGetCssClass;
window.ftLatinToTengwarQwertyOutsideTags = ftLatinToTengwarQwertyOutsideTags;
window.ftLatinToDwarvishRunicOutsideTags = ftLatinToDwarvishRunicOutsideTags;
window.ftLatinToCelestialEnochianOutsideTags = ftLatinToCelestialEnochianOutsideTags;
window.ftLanguageWrapPreview = ftLanguageWrapPreview;
window.ftLanguageRenderParsed = ftLanguageRenderParsed;
window.ftUpdateInventoryDescriptionScriptPreview = ftUpdateInventoryDescriptionScriptPreview;
window.ftMountWrittenDescriptions?.();

// Run-once-guarded document delegated listeners (pure DOM; read window.ft… at fire time).
if (typeof document !== 'undefined' && !document.ftInventoryScriptPreviewChangeBound) {
    document.addEventListener('change', function (e) {
        const target = e.target;
        if (!(target instanceof Element) || target.id !== 'inventory_written_in_language') {
            return;
        }
        const invForm = target.closest<HTMLElement>('.inventory-container-form');
        if (invForm && typeof window.ftUpdateInventoryDescriptionScriptPreview === 'function') {
            window.ftUpdateInventoryDescriptionScriptPreview(invForm);
        }
    });
    document.ftInventoryScriptPreviewChangeBound = true;
}

if (typeof document !== 'undefined' && !document.ftWrittenTranslateClickBound) {
    document.addEventListener('click', function (e) {
        const target = e.target;
        const btn = target instanceof Element ? target.closest<HTMLElement>('.ft-written-translate-btn') : null;
        if (!btn) {
            return;
        }
        const root = btn.closest('.ft-written-description');
        if (!root || root.getAttribute('data-ft-translate') !== '1') {
            return;
        }

        const plain = root.querySelector('.ft-written-plain');
        const encoded = root.querySelector('.ft-written-encoded-layer');
        const willShowPlain = !root.classList.contains('ft-plain-visible');
        btn.dataset.ftTranslateLabel ??= btn.textContent || 'Translate';

        if (willShowPlain) {
            root.classList.add('ft-plain-visible');
        } else {
            root.classList.remove('ft-plain-visible');
        }
        btn.setAttribute('data-ft-showing', willShowPlain ? 'plain' : 'encoded');
        btn.textContent = willShowPlain ? 'Show original' : btn.dataset.ftTranslateLabel;
        btn.setAttribute('aria-pressed', willShowPlain ? 'true' : 'false');
        if (plain) {
            plain.setAttribute('aria-hidden', willShowPlain ? 'false' : 'true');
        }
        if (encoded) {
            encoded.setAttribute('aria-hidden', willShowPlain ? 'true' : 'false');
        }
    });
    document.ftWrittenTranslateClickBound = true;
}

export {};
